import React, { useEffect, useState } from 'react';
import {
    Table,
    Button,
    Input,
    InputNumber,
    Select,
    Modal,
    Form,
    Switch,
    Space,
    message,
    Card,
    Row,
    Col,
    Typography,
    Avatar,
    Tag,
    Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    SearchOutlined,
    ReloadOutlined,
    LinkOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import axiosInstance from '../../../core/services/axiosConfig';
import { apis } from '../../../core/services/params';
import {
    ServiceWarrantyDTO,
    ServiceWarrantyFormValues,
    ResponseModel,
    PagedServiceWarrantyResponse,
} from './types';
import { toProductImageUrl, toProductPageUrl } from './utils';

const { Text } = Typography;

const DEFAULT_PAGE_SIZE = 10;

const formatDate = (value?: string) => (value ? dayjs(value).format('DD MMM YYYY') : '-');

const renderTagList = (values: string[] | undefined, color: string, maxShow = 2) => {
    if (!values || values.length === 0) {
        return <Text type="secondary">-</Text>;
    }
    const shown = values.slice(0, maxShow);
    const rest = values.slice(maxShow);
    return (
        <Space size={[4, 4]} wrap>
            {shown.map((value) => (
                <Tag color={color} key={value} style={{ marginRight: 0 }}>
                    {value}
                </Tag>
            ))}
            {rest.length > 0 && (
                <Tooltip title={rest.join(', ')}>
                    <Tag style={{ marginRight: 0 }}>+{rest.length}</Tag>
                </Tooltip>
            )}
        </Space>
    );
};

const ServiceWarrantyCatalogList = () => {
    const [items, setItems] = useState<ServiceWarrantyDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [editingItem, setEditingItem] = useState<ServiceWarrantyDTO | null>(null);
    const [form] = Form.useForm<ServiceWarrantyFormValues>();

    const [serviceIdFilter, setServiceIdFilter] = useState('');
    const [productIdFilter, setProductIdFilter] = useState('');
    const [serviceProductIdFilter, setServiceProductIdFilter] = useState('');
    const [brandNameFilter, setBrandNameFilter] = useState('');
    const [categoryNameFilter, setCategoryNameFilter] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [total, setTotal] = useState(0);

    const fetchItems = async (overrides?: {
        page?: number;
        size?: number;
        serviceId?: string;
        productId?: string;
        serviceProductId?: string;
        brandName?: string;
        categoryName?: string;
    }) => {
        const requestPage = overrides?.page ?? page;
        const requestSize = overrides?.size ?? pageSize;
        const requestServiceId = overrides?.serviceId ?? serviceIdFilter;
        const requestProductId = overrides?.productId ?? productIdFilter;
        const requestServiceProductId = overrides?.serviceProductId ?? serviceProductIdFilter;
        const requestBrandName = overrides?.brandName ?? brandNameFilter;
        const requestCategoryName = overrides?.categoryName ?? categoryNameFilter;
        setLoading(true);
        try {
            const response = await axiosInstance.get<ResponseModel<PagedServiceWarrantyResponse>>(
                apis.GET_SERVICE_WARRANTIES,
                {
                    params: {
                        serviceId: requestServiceId || undefined,
                        productId: requestProductId || undefined,
                        serviceProductId: requestServiceProductId || undefined,
                        brandName: requestBrandName || undefined,
                        categoryName: requestCategoryName || undefined,
                        page: requestPage,
                        size: requestSize,
                    },
                }
            );
            if (response.data?.status) {
                const result = response.data.data;
                setItems(result?.records || []);
                setTotal(result?.total || 0);
                setPage(result?.page ?? requestPage);
                setPageSize(result?.size ?? requestSize);
            } else {
                message.error(response.data?.statusDescription || 'Failed to fetch service warranties');
            }
        } catch (error: any) {
            console.error('Error fetching service warranties:', error);
            message.error(error?.response?.data?.statusDescription || 'Failed to fetch service warranties');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems();
        // eslint-disable-next-line
    }, []);

    const handleSearch = () => {
        fetchItems({ page: 1 });
    };

    const handleResetFilters = () => {
        setServiceIdFilter('');
        setProductIdFilter('');
        setServiceProductIdFilter('');
        setBrandNameFilter('');
        setCategoryNameFilter('');
        fetchItems({
            page: 1,
            serviceId: '',
            productId: '',
            serviceProductId: '',
            brandName: '',
            categoryName: '',
        });
    };

    const handleTableChange = (newPage: number, newPageSize: number) => {
        fetchItems({ page: newPage, size: newPageSize });
    };

    const openCreateModal = () => {
        setEditingItem(null);
        setModalVisible(true);
    };

    const openEditModal = (record: ServiceWarrantyDTO) => {
        setEditingItem(record);
        setModalVisible(true);
    };

    const closeModal = () => {
        setModalVisible(false);
        setEditingItem(null);
    };

    useEffect(() => {
        if (!modalVisible) {
            return;
        }
        if (editingItem) {
            form.setFieldsValue({
                productId: editingItem.productId,
                serviceProductId: editingItem.serviceProductId,
                originalWarrantyMonths: editingItem.originalWarrantyMonths,
                price: editingItem.price,
                coveredParts: editingItem.coveredParts,
                notCoveredParts: editingItem.notCoveredParts,
                extensionOptions: editingItem.extensionOptions,
                claimReasons: editingItem.claimReasons,
                claimRejectionReasons: editingItem.claimRejectionReasons,
                resolutionModes: editingItem.resolutionModes,
            });
        } else {
            form.resetFields();
        }
    }, [modalVisible, editingItem]);

    const handleSubmit = async (values: ServiceWarrantyFormValues) => {
        setModalLoading(true);
        try {
            const payload: ServiceWarrantyDTO = {
                productId: values.productId,
                serviceProductId: values.serviceProductId,
                originalWarrantyMonths: values.originalWarrantyMonths,
                price: values.price,
                coveredParts: values.coveredParts,
                notCoveredParts: values.notCoveredParts,
                extensionOptions: values.extensionOptions,
                claimReasons: values.claimReasons,
                claimRejectionReasons: values.claimRejectionReasons,
                resolutionModes: values.resolutionModes,
            };

            const response = await axiosInstance.post<ResponseModel<ServiceWarrantyDTO>>(
                apis.UPSERT_SERVICE_WARRANTY,
                payload
            );

            if (response.data?.status) {
                message.success(editingItem ? 'Service warranty updated successfully' : 'Service warranty created successfully');
                closeModal();
                fetchItems();
            } else {
                message.error(response.data?.statusDescription || 'Failed to save service warranty');
            }
        } catch (error: any) {
            console.error('Error saving service warranty:', error);
            message.error(error?.response?.data?.statusDescription || 'Failed to save service warranty');
        } finally {
            setModalLoading(false);
        }
    };

    const columns: ColumnsType<ServiceWarrantyDTO> = [
        {
            title: 'Product',
            key: 'product',
            width: 260,
            render: (_, record) => (
                <Space align="start">
                    <Avatar
                        shape="square"
                        src={toProductImageUrl(record.productInfo?.mainImageLink)}
                        size={48}
                        style={{ border: '1px solid #f0f0f0', flexShrink: 0 }}
                    >
                        {record.productId?.slice(0, 2)}
                    </Avatar>
                    <div>
                        <Text strong>{record.productId}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Service Product ID: {record.serviceProductId || '-'}
                        </Text>
                        <br />
                        <Text style={{ fontSize: 12 }}>{record.productInfo?.brandName || 'Brand unknown'}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {record.productInfo?.categoryName || record.productInfo?.categoryNames?.[0] || 'Category unknown'}
                        </Text>
                        {record.productInfo?.productUrl && (
                            <>
                                <br />
                                <a
                                    href={toProductPageUrl(record.productInfo.productUrl)}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ fontSize: 12 }}
                                >
                                    <LinkOutlined /> View Product
                                </a>
                            </>
                        )}
                    </div>
                </Space>
            ),
        },
        {
            title: 'Service Info',
            key: 'serviceInfo',
            width: 160,
            render: (_, record) => (
                <>
                    <Text strong>{record.serviceId || '-'}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Created {formatDate(record.createdAt)}
                    </Text>
                    {record.createdBy && (
                        <>
                            <br />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                by {record.createdBy}
                            </Text>
                        </>
                    )}
                </>
            ),
        },
        {
            title: 'Warranty & Price',
            key: 'warrantyPrice',
            width: 170,
            render: (_, record) => (
                <>
                    <Text>{record.originalWarrantyMonths} month{record.originalWarrantyMonths === 1 ? '' : 's'} original</Text>
                    <br />
                    <Text strong>{record.price != null ? `₹${record.price}` : 'Price not set'}</Text>
                    {record.extensionOptions && record.extensionOptions.length > 0 && (
                        <>
                            <br />
                            <Tag color="gold" style={{ marginTop: 4 }}>
                                {record.extensionOptions.length} extension plan{record.extensionOptions.length === 1 ? '' : 's'}
                            </Tag>
                        </>
                    )}
                </>
            ),
        },
        {
            title: 'Coverage',
            key: 'coverage',
            width: 220,
            render: (_, record) => (
                <>
                    <Text type="secondary" style={{ fontSize: 12 }}>Covered</Text>
                    <div style={{ marginBottom: 6, marginTop: 2 }}>{renderTagList(record.coveredParts, 'green')}</div>
                    <Text type="secondary" style={{ fontSize: 12 }}>Not Covered</Text>
                    <div style={{ marginTop: 2 }}>{renderTagList(record.notCoveredParts, 'red')}</div>
                </>
            ),
        },
        {
            title: 'Claims Setup',
            key: 'claimsSetup',
            width: 220,
            render: (_, record) => (
                <>
                    <Text type="secondary" style={{ fontSize: 12 }}>Claim Reasons</Text>
                    <div style={{ marginBottom: 6, marginTop: 2 }}>{renderTagList(record.claimReasons, 'blue')}</div>
                    <Text type="secondary" style={{ fontSize: 12 }}>Resolution Modes</Text>
                    <div style={{ marginTop: 2 }}>{renderTagList(record.resolutionModes, 'purple')}</div>
                </>
            ),
        },
        {
            title: 'Updated',
            key: 'updated',
            width: 140,
            render: (_, record) => (
                <>
                    <Text style={{ fontSize: 12 }}>{formatDate(record.updatedAt)}</Text>
                    {record.updatedBy && (
                        <>
                            <br />
                            <Text type="secondary" style={{ fontSize: 12 }}>by {record.updatedBy}</Text>
                        </>
                    )}
                </>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 130,
            render: (_, record) => (
                <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => openEditModal(record)}
                    style={{ padding: 0, height: 'auto' }}
                >
                    Edit
                </Button>
            ),
        },
    ];

    return (
        <div style={{ padding: '16px 20px' }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
            }}>
                <Text strong style={{ fontSize: 16 }}>
                    Service Warranty Catalog ({total})
                </Text>
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                    Add New
                </Button>
            </div>

            <Space style={{ marginBottom: 16 }} wrap>
                <Input
                    placeholder="Filter by service ID"
                    value={serviceIdFilter}
                    onChange={(e) => setServiceIdFilter(e.target.value)}
                    onPressEnter={handleSearch}
                    style={{ width: 200 }}
                    allowClear
                />
                <Input
                    placeholder="Filter by product ID"
                    value={productIdFilter}
                    onChange={(e) => setProductIdFilter(e.target.value)}
                    onPressEnter={handleSearch}
                    style={{ width: 200 }}
                    allowClear
                />
                <Input
                    placeholder="Filter by service product ID"
                    value={serviceProductIdFilter}
                    onChange={(e) => setServiceProductIdFilter(e.target.value)}
                    onPressEnter={handleSearch}
                    style={{ width: 200 }}
                    allowClear
                />
                <Input
                    placeholder="Filter by brand name"
                    value={brandNameFilter}
                    onChange={(e) => setBrandNameFilter(e.target.value)}
                    onPressEnter={handleSearch}
                    style={{ width: 200 }}
                    allowClear
                />
                <Input
                    placeholder="Filter by category name"
                    value={categoryNameFilter}
                    onChange={(e) => setCategoryNameFilter(e.target.value)}
                    onPressEnter={handleSearch}
                    style={{ width: 200 }}
                    allowClear
                />
                <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                    Search
                </Button>
                <Button icon={<ReloadOutlined />} onClick={handleResetFilters}>
                    Reset
                </Button>
            </Space>

            <Card style={{ overflow: 'hidden' }}>
                <Table<ServiceWarrantyDTO>
                    rowKey="serviceId"
                    columns={columns}
                    dataSource={items}
                    loading={loading}
                    scroll={{ x: 1300 }}
                    pagination={{
                        current: page,
                        pageSize,
                        total,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '25', '50', '100'],
                        showTotal: (t) => `${t} total`,
                        onChange: handleTableChange,
                    }}
                    size="small"
                />
            </Card>

            <Modal
                title={
                    editingItem
                        ? `Edit Service Warranty - ${editingItem.productId} (${editingItem.serviceProductId})`
                        : 'Add Service Warranty'
                }
                open={modalVisible}
                onCancel={closeModal}
                footer={null}
                width={720}
                destroyOnHidden
            >
                <Form<ServiceWarrantyFormValues>
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    style={{ marginTop: 20 }}
                >
                    <Form.Item
                        label="Product ID"
                        name="productId"
                        rules={[{ required: true, message: 'Please enter product ID' }]}
                        normalize={(value: string) => value?.toUpperCase()}
                    >
                        <Input placeholder="e.g. MSN12345" disabled={!!editingItem} />
                    </Form.Item>

                    <Form.Item
                        label="Service Product ID"
                        name="serviceProductId"
                        rules={[{ required: true, message: 'Please enter service product ID' }]}
                        normalize={(value: string) => value?.toUpperCase()}
                    >
                        <Input placeholder="e.g. MSN67890" />
                    </Form.Item>

                    {editingItem && (
                        <Card size="small" style={{ marginBottom: 24, background: '#fafafa' }}>
                            <Space size={16}>
                                <Avatar
                                    shape="square"
                                    src={toProductImageUrl(editingItem.productInfo?.mainImageLink)}
                                    size={64}
                                    style={{ border: '1px solid #f0f0f0', flexShrink: 0 }}
                                >
                                    {editingItem.productId?.slice(0, 2)}
                                </Avatar>
                                <div>
                                    <Text strong>{editingItem.productInfo?.brandName || 'Brand unknown'}</Text>
                                    <br />
                                    <Text type="secondary">
                                        {editingItem.productInfo?.categoryName
                                            || editingItem.productInfo?.categoryNames?.[0]
                                            || 'Category unknown'}
                                    </Text>
                                </div>
                            </Space>
                        </Card>
                    )}
                    {!editingItem && (
                        <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
                            Brand, category and product image are looked up automatically from the product ID
                            once saved.
                        </Text>
                    )}

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Original Warranty (months)"
                                name="originalWarrantyMonths"
                                rules={[{ required: true, message: 'Please enter original warranty duration' }]}
                            >
                                <InputNumber min={1} style={{ width: '100%' }} placeholder="e.g. 6" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Price" name="price">
                                <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g. 499" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item label="Covered Parts" name="coveredParts">
                        <Select mode="tags" placeholder="Type and press enter, e.g. Motor, Battery" style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item label="Not Covered Parts" name="notCoveredParts">
                        <Select mode="tags" placeholder="Type and press enter, e.g. Casing" style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item label="Claim Reasons" name="claimReasons">
                        <Select mode="tags" placeholder="Type and press enter, e.g. defective, damaged" style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item label="Claim Rejection Reasons" name="claimRejectionReasons">
                        <Select mode="tags" placeholder="Type and press enter, e.g. blur image" style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item label="Resolution Modes" name="resolutionModes">
                        <Select mode="tags" placeholder="Type and press enter, e.g. pickup, technician" style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item label="Extension Options">
                        <Form.List name="extensionOptions">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <Row gutter={8} key={key} align="middle" style={{ marginBottom: 8 }}>
                                            <Col span={5}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'durationMonths']}
                                                    rules={[{ required: true, message: 'Required' }]}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <InputNumber min={1} placeholder="Months" style={{ width: '100%' }} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={4}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'available']}
                                                    valuePropName="checked"
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <Switch checkedChildren="Available" unCheckedChildren="Unavailable" />
                                                </Form.Item>
                                            </Col>
                                            <Col span={7}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'claimWindow']}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <Input placeholder="Claim window, e.g. 2 months after order closed" />
                                                </Form.Item>
                                            </Col>
                                            <Col span={5}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'price']}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <InputNumber min={0} placeholder="Price" style={{ width: '100%' }} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={3}>
                                                <Button
                                                    type="text"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => remove(name)}
                                                />
                                            </Col>
                                        </Row>
                                    ))}
                                    <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} style={{ width: '100%' }}>
                                        Add Extension Option
                                    </Button>
                                </>
                            )}
                        </Form.List>
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0, textAlign: 'right', marginTop: 24 }}>
                        <Space>
                            <Button onClick={closeModal}>Cancel</Button>
                            <Button type="primary" htmlType="submit" loading={modalLoading}>
                                {editingItem ? 'Save Changes' : 'Create'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ServiceWarrantyCatalogList;
